import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Container,
  Typography,
  IconButton,
  Badge,
  InputBase,
  useTheme,
} from '@mui/material';
import { ArrowBack as ArrowBackIcon, Search, Close } from '@mui/icons-material';
import { useCart } from '../../cart/CartManager';
import SearchSuggestions from './SearchSuggestions';
import { searchApi, SearchSuggestion } from '../../api/search';

interface PageHeaderProps {
  title: string;
  onBackClick?: () => void;
  showSearch?: boolean;
  showCart?: boolean;
}

const PageHeader: React.FC<PageHeaderProps> = ({
  title,
  onBackClick,
  showSearch = true,
  showCart = true,
}) => {
  const navigate = useNavigate();
  const theme = useTheme();
  const { t } = useTranslation();
  const { cartItems } = useCart();
  const searchInputRef = useRef<HTMLInputElement>(null);

  // Search state
  const [searchBarVisible, setSearchBarVisible] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [suggestions, setSuggestions] = useState<SearchSuggestion[]>([]);
  const [suggestionsLoading, setSuggestionsLoading] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);

  // Debounced search suggestions
  useEffect(() => {
    if (!searchQuery.trim()) {
      setSuggestions([]);
      setShowSuggestions(false);
      return;
    }

    const timeoutId = setTimeout(async () => {
      setSuggestionsLoading(true);
      try {
        const response = await searchApi.getSearchSuggestions(searchQuery);
        if (response.success) {
          setSuggestions(response.suggestions || []);
          setShowSuggestions(true);
        }
      } catch (error) {
        console.error('Error fetching suggestions:', error);
        setSuggestions([]);
      } finally {
        setSuggestionsLoading(false);
      }
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [searchQuery]);

  const handleBackClick = () => {
    if (onBackClick) {
      onBackClick();
    } else {
      navigate(-1);
    }
  };

  const handleOpenSearchBar = () => {
    setSearchBarVisible(true);
    // Focus the input after the component renders
    setTimeout(() => {
      if (searchInputRef.current) {
        searchInputRef.current.focus();
      }
    }, 100);
  };

  const handleCloseSearchBar = () => {
    setSearchBarVisible(false);
    setSearchQuery('');
    setShowSuggestions(false);
  };

  const handleSearchInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(event.target.value);
  };

  const handleSearchSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/search?q=${encodeURIComponent(searchQuery.trim())}`);
      handleCloseSearchBar();
    }
  };

  const handleSuggestionClick = (suggestion: SearchSuggestion) => {
    navigate(`/search?q=${encodeURIComponent(suggestion.text)}`);
    handleCloseSearchBar();
  };

  const handleCartClick = () => {
    navigate('/cart');
  };

  return (
    <Box
      sx={{
        backgroundColor: 'white',
        borderBottom: '1px solid #E7E3D5',
        position: { xs: 'fixed', md: 'sticky' },
        top: { xs: 0, md: 0 },
        left: { xs: 0, md: 'auto' },
        right: { xs: 0, md: 'auto' },
        zIndex: 1000,
        width: { xs: '100%', md: 'auto' },
      }}
    >
      <Container maxWidth="lg">
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: { xs: 'space-between', md: 'flex-start' },
            py: 1,
          }}
        >
          {/* Left: Back Button */}
          <IconButton
            onClick={handleBackClick}
            sx={{
              color: theme.palette.custom.brown,
            }}
          >
            <ArrowBackIcon />
          </IconButton>

          {/* Center: Title */}
          <Typography
            sx={{
              fontSize: '16px',
              fontWeight: 'bold',
              color: theme.palette.custom.brown,
              flex: { xs: 1, md: 0 },
              textAlign: { xs: 'center', md: 'left' },
              ml: { xs: 0, md: 2 },
              whiteSpace: 'nowrap',
            }}
          >
            {title}
          </Typography>

          {/* Right: Search and Cart Icons - Mobile Only */}
          <Box sx={{ display: { xs: 'flex', md: 'none' }, alignItems: 'center', gap: 1 }}>
            {/* Search Icon */}
            {showSearch && (
              <IconButton
                onClick={handleOpenSearchBar}
                sx={{
                  color: theme.palette.custom.brown,
                }}
              >
                <Search />
              </IconButton>
            )}

            {/* Cart Icon */}
            {showCart && (
              <IconButton
                onClick={handleCartClick}
                sx={{
                  color: theme.palette.custom.brown,
                }}
              >
                <Badge badgeContent={cartItems.length} color="error" overlap="circular">
                  <img
                    src="/icons/ic_cart.svg"
                    alt="Cart"
                    style={{
                      width: 24,
                      height: 24,
                      display: 'block',
                    }}
                  />
                </Badge>
              </IconButton>
            )}
          </Box>
        </Box>
      </Container>

      {/* Mobile Search Bar - Appears at top when search icon is clicked */}
      {searchBarVisible && (
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            zIndex: 1300,
            bgcolor: 'white',
            borderBottom: '1px solid #E7E3D5',
            px: 2,
            py: 1,
          }}
        >
          <Box
            component="form"
            onSubmit={handleSearchSubmit}
            sx={{
              display: 'flex',
              alignItems: 'center',
              width: '100%',
              maxWidth: 600,
              mx: 'auto',
              border: '1px solid #a98c74',
              borderRadius: 2,
              bgcolor: 'white',
              position: 'relative',
            }}
          >
            {/* Close Button */}
            <IconButton
              onClick={handleCloseSearchBar}
              sx={{ p: '10px', color: '#a98c74', mr: 1 }}
              aria-label="close search"
            >
              <Close />
            </IconButton>
            
            <InputBase
              ref={searchInputRef}
              value={searchQuery}
              onChange={handleSearchInputChange}
              placeholder={t('common.search')}
              sx={{ ml: 2, flex: 1, color: '#a98c74' }}
              inputProps={{ 'aria-label': 'search' }}
            />
            <IconButton
              type="submit"
              sx={{ p: '10px', color: '#a98c74' }}
              aria-label="search"
            >
              <Search />
            </IconButton>
            
            {/* Search Suggestions */}
            <SearchSuggestions
              suggestions={suggestions}
              loading={suggestionsLoading}
              query={searchQuery}
              visible={showSuggestions}
              onSuggestionClick={handleSuggestionClick}
            />
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default PageHeader;
